"use client";

import {
  ArrowRight,
  ArrowUpRight,
  BookOpen,
  Bookmark,
  Bot,
  Box,
  Brain,
  Building2,
  Clock,
  Code,
  Columns3,
  Compass,
  File,
  FileText,
  FlaskConical,
  FolderOpen,
  Gauge,
  History,
  Image as ImageIcon,
  Inbox,
  KeyRound,
  Layers,
  LayoutGrid,
  Link2,
  ListChecks,
  type LucideIcon,
  MessageSquare,
  MessagesSquare,
  Minus,
  PencilLine,
  Play,
  Plus,
  Power,
  PowerOff,
  RefreshCw,
  Rocket,
  Rows3,
  Search,
  ShieldCheck,
  Send,
  SquareTerminal,
  Table2,
  Tag,
  User,
  Workflow,
  Zap,
} from "lucide-react";
import Link from "next/link";
import { createContext, useContext, useState, type ReactNode } from "react";
import { CodeBlock } from "@/components/ai-elements/code-block";
import { FileCell } from "@/components/DataTable/FileCell";
import { ReferenceValue } from "@/components/DataTable/ReferenceValue";
import { Badge } from "@/components/shadcn/badge";
import { Button } from "@/components/shadcn/button";
import { Skeleton } from "@/components/shadcn/skeleton";
import { isAxiosError } from "axios";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/shadcn/table";
import { ModelSelectCompact } from "@/components/SpotflowV2/CustomNodes/BaseNode/ModelSelectCompact";
import { NoxusAlert } from "@/components/UI/NoxusAlert";
import { NoxusIcon } from "@/components/UI/NoxusIcon";
import { getUrlForEntity } from "@/components/UI/EntityReferenceRow";
import { COLOUR_TOKENS } from "@/utils/colourTokens";
import { UUID_RE, spotFileId } from "@/utils/generic";
import { ORG_PERMISSIONS } from "@/constants/permissions";
import { usePermissions } from "@/hooks/usePermissions";
import { isRecord } from "@/utils/isRecord";
import { DATA_KEYS, useRemoteData } from "@/utils/useRemoteData";
import { formatMoney, providerCurrency } from "@/utils/billingCurrency";
import type { OperationsLimit, ProviderCostLimit } from "@/generated";
import { useSpotUser } from "@/utils/spotUser";
import { cn } from "@/lib/utils";
import { useEntityNameLookup } from "../entityNames";
import { SourceIcon } from "@/components/KnowledgeBases/sourceIcons";
import { useKBSyncList } from "@/hooks/useKBSync";
import { formatSyncResourceLabel } from "@/services/kbSync";
import { describeSchedule } from "./triggerSummary";
import { findNodeByHandle, useApprovalFlowNodes } from "./flowNodes";
import type { ToolArea } from "../toolDisplay";
import {
  choiceDescription,
  choiceLabel,
  codeLanguage,
  cellText,
  formatValue,
  humanize,
  isEmptyValue,
  LOOKUP_ROWS,
  lookedUpRows,
  PREVIEW_ROWS,
  plural,
  previewRows,
  quoteText,
  titleColumn,
  withTableColumns,
  type CodeLanguage,
  type RowsPreview,
} from "./approvalValues";
import {
  APPROVAL_RENDERERS,
  type ApprovalView,
  type ArgField,
  type EntityKind,
  type EntityRef,
  type FieldDisplay,
  type StepOp,
  type ToolApproval,
} from "./approvalView";

const OP_ICON: Record<StepOp | "trigger", LucideIcon> = {
  add: Plus,
  edit: PencilLine,
  remove: Minus,
  run: Play,
  send: Send,
  go: ArrowRight,
  save: Bookmark,
  link: Link2,
  on: Power,
  off: PowerOff,
  trigger: Clock,
};

// The round op marker every approval row opens with, workflow steps included.
export function OpBadge({
  op,
  className,
}: {
  op: StepOp | "trigger";
  className?: string;
}) {
  const Icon = OP_ICON[op];
  return (
    <span
      className={cn(
        "flex h-4 w-4 shrink-0 items-center justify-center rounded-full border border-border bg-background2 text-muted-foreground",
        className
      )}
    >
      <Icon className="size-2.5" />
    </span>
  );
}

const ENTITY: Record<
  EntityKind,
  // ``every`` names the kind when the call names no one entity of it (all of
  // an inbox's cases).
  { icon: LucideIcon; noun: string; area?: ToolArea; every?: string }
> = {
  agent: { icon: Bot, noun: "Agent", area: "agents" },
  knowledge_base: { icon: BookOpen, noun: "Knowledge base", area: "knowledge" },
  table: { icon: Table2, noun: "Table", area: "tables" },
  workflow: { icon: Workflow, noun: "Workflow", area: "workflows" },
  run: { icon: Play, noun: "Run", area: "workflows" },
  conversation: { icon: MessagesSquare, noun: "Conversation", area: "agents" },
  test_suite: { icon: FlaskConical, noun: "Test suite", area: "agents" },
  test_case: { icon: ListChecks, noun: "Test case", area: "agents" },
  evaluation_run: { icon: FlaskConical, noun: "Test run", area: "agents" },
  evaluator: { icon: Gauge, noun: "Evaluator", area: "agents" },
  deployment: { icon: Rocket, noun: "Deployment", area: "agents" },
  trigger: { icon: Zap, noun: "Trigger", area: "agents" },
  version: { icon: History, noun: "Version" },
  file: { icon: File, noun: "File", area: "files" },
  document: { icon: FileText, noun: "Document", area: "knowledge" },
  sync: { icon: RefreshCw, noun: "Sync", area: "knowledge" },
  sandbox: { icon: SquareTerminal, noun: "Sandbox", area: "sandboxes" },
  workspace: { icon: Building2, noun: "Workspace" },
  member: { icon: User, noun: "Member" },
  key: { icon: KeyRound, noun: "API key" },
  column: { icon: Columns3, noun: "Column", area: "tables" },
  rows: { icon: Rows3, noun: "Rows", area: "tables" },
  runs: { icon: Play, noun: "Runs", area: "workflows" },
  node: { icon: Box, noun: "Node", area: "workflows" },
  layout: { icon: LayoutGrid, noun: "Layout", area: "workflows" },
  statement: { icon: Table2, noun: "SQL statement", area: "tables" },
  page: { icon: Compass, noun: "Page" },
  schedule: { icon: Clock, noun: "Scheduled task" },
  memory: { icon: Brain, noun: "Memory" },
  message: { icon: MessageSquare, noun: "Message" },
  image: { icon: ImageIcon, noun: "Image", area: "files" },
  code: { icon: Code, noun: "Code" },
  search: { icon: Search, noun: "Web search" },
  inbox: { icon: Inbox, noun: "Inbox", area: "cases" },
  case: { icon: FolderOpen, noun: "Case", area: "cases", every: "Every case" },
  property: { icon: Tag, noun: "Property", area: "cases" },
  property_group: { icon: Layers, noun: "Property group", area: "cases" },
  role: { icon: ShieldCheck, noun: "Role" },
  usage_limit: { icon: Gauge, noun: "Usage limit" },
};

// Same hues as the "Always allow" pill, as an icon tile like the node tiles.
const AREA_TILE: Record<ToolArea, string> = {
  workflows: "bg-slate-100 text-slate-700",
  tables: "bg-blue-50 text-blue-700",
  knowledge: "bg-blue-50 text-blue-700",
  agents: "bg-emerald-50 text-emerald-700",
  files: "bg-sky-50 text-sky-700",
  sandboxes: "bg-fuchsia-50 text-fuchsia-700",
  cases: "bg-purple-50 text-purple-700",
};

function EntityTile({ kind }: { kind: EntityKind }) {
  const { icon: Icon, area } = ENTITY[kind];
  return (
    <span
      className={cn(
        "flex h-5 w-5 shrink-0 items-center justify-center rounded",
        area ? AREA_TILE[area] : "bg-muted text-muted-foreground"
      )}
    >
      <Icon className="size-3.5" />
    </span>
  );
}

interface EntityDisplay {
  name: string;
  // A NoxusIcon key or file ref: the entity's own icon, else its kind's.
  icon: string | null;
  href: string | null;
  // Its name is still being fetched: the card shows a placeholder, not the noun.
  loading?: boolean;
}

const KIND_ICON: Partial<Record<EntityKind, string>> = {
  agent: "coworker",
  knowledge_base: "kb",
  table: "table",
  workflow: "workflow",
  test_suite: "flask",
  inbox: "inbox",
};

// Every query but the one for this entity's kind stays disabled, so a card
// fetches only what it shows and react-query shares it across cards.
function useEntityDisplay(entity: EntityRef): EntityDisplay {
  const names = useEntityNameLookup();
  const { spotGroup } = useSpotUser();
  const g = spotGroup.id;
  const id = entity.id ?? "";
  // Before the workspace loads there is no group id to query under.
  const is = (kind: EntityKind) => !!g && !!entity.id && entity.kind === kind;

  const agentQuery = useRemoteData(
    DATA_KEYS.ASSISTANT_ID,
    { path: { group_id: g, assistant_id: id } },
    { enabled: is("agent"), retry: retryUnlessGone }
  );
  const kbQuery = useRemoteData(
    DATA_KEYS.KNOWLEDGE_BASES_V2_ID,
    { path: { group_id: g, knowledge_base_id: id } },
    { enabled: is("knowledge_base"), retry: retryUnlessGone }
  );
  const tableQuery = useRemoteData(
    DATA_KEYS.TABLES_ID,
    { path: { group_id: g, table_id: id } },
    { enabled: is("table"), retry: retryUnlessGone }
  );
  const workflowQuery = useRemoteData(
    DATA_KEYS.WORKFLOWS_ID,
    { path: { group_id: g, workflow_id: id } },
    { enabled: is("workflow"), retry: retryUnlessGone }
  );
  const conversationQuery = useRemoteData(
    DATA_KEYS.CONVERSATIONS_ID,
    { path: { group_id: g, conversation_id: id } },
    { enabled: is("conversation"), retry: retryUnlessGone }
  );
  const suiteQuery = useRemoteData(
    DATA_KEYS.TEST_SUITE,
    { path: { group_id: g, test_suite_id: id } },
    { enabled: is("test_suite"), retry: retryUnlessGone }
  );
  const inboxQuery = useRemoteData(
    DATA_KEYS.INBOXES_ID,
    { path: { group_id: g, inbox_id: id } },
    { enabled: is("inbox") }
  );
  const caseQuery = useRemoteData(
    DATA_KEYS.CASES_ID,
    { path: { group_id: g, case_id: id } },
    { enabled: is("case") }
  );
  // A case is also named by its display id ("INV-003"): the list's search
  // finds it, and only an exact match names it.
  const displayId = entity.kind === "case" && !entity.id ? entity.name : "";
  const casesByDisplayId = useRemoteData(
    DATA_KEYS.CASES,
    {
      path: { group_id: g },
      query: { search: displayId, page: 1, size: LOOKUP_ROWS },
    },
    { enabled: !!g && !!displayId }
  );
  // An entity that lives in another is read there.
  const parent = entity.parent ?? "";
  const inParent = (kind: EntityKind) => is(kind) && !!parent;
  const versionQuery = useRemoteData(
    DATA_KEYS.ASSISTANT_VERSION,
    { path: { group_id: g, assistant_id: parent, version_id: id } },
    { enabled: inParent("version"), retry: retryUnlessGone }
  );
  const deploymentsQuery = useRemoteData(
    DATA_KEYS.LIST_DEPLOYMENTS,
    { path: { group_id: g, assistant_id: parent } },
    { enabled: inParent("deployment"), retry: retryUnlessGone }
  );
  const columnTableQuery = useRemoteData(
    DATA_KEYS.TABLES_ID,
    { path: { group_id: g, table_id: parent } },
    { enabled: inParent("column"), retry: retryUnlessGone }
  );
  const limitsQuery = useRemoteData(
    DATA_KEYS.BILLING_LIMITS,
    {},
    { enabled: is("usage_limit") }
  );
  const billingCurrency = useRemoteData(DATA_KEYS.BILLING_CURRENCY, undefined, {
    enabled: is("usage_limit"),
  }).data;
  const parentFlowQuery = useRemoteData(
    DATA_KEYS.WORKFLOWS_ID,
    { path: { group_id: g, workflow_id: parent } },
    { enabled: inParent("run"), retry: retryUnlessGone }
  );
  // A test and a test run open on their set, under the set's agent.
  const parentSuiteQuery = useRemoteData(
    DATA_KEYS.TEST_SUITE,
    { path: { group_id: g, test_suite_id: parent } },
    {
      enabled: inParent("test_case") || inParent("evaluation_run"),
      retry: retryUnlessGone,
    }
  );
  const parentFlow = parentFlowQuery.data;
  const parentSuite = parentSuiteQuery.data;
  const syncsQuery = useKBSyncList(parent, inParent("sync"));
  const agent = agentQuery.data;
  const kb = kbQuery.data;
  const table = tableQuery.data;
  const workflow = workflowQuery.data;
  const conversation = conversationQuery.data;
  const suite = suiteQuery.data;
  const inbox = inboxQuery.data;
  const caseItem =
    caseQuery.data ??
    casesByDisplayId.data?.items?.find((c) => c.display_id === displayId);

  // A conversation wears its agent's icon, as the chat pages show it.
  const conversationAgent = useRemoteData(
    DATA_KEYS.ASSISTANT_ID,
    {
      path: { group_id: g, assistant_id: conversation?.assistant_id ?? "" },
    },
    { enabled: !!g && !!conversation?.assistant_id }
  ).data;

  // A workspace is named by an id or a name in one arg (switch_workspace):
  // an id reads as the name of the workspace it is, among the user's own or,
  // for an org admin switching into another, all of them.
  const workspaceRef =
    entity.kind === "workspace" ? (entity.id ?? entity.name ?? "") : "";
  const myGroups = useRemoteData(DATA_KEYS.MY_GROUPS, undefined, {
    enabled: UUID_RE.test(workspaceRef),
  }).data?.items;
  const mine = myGroups?.find((item) => item.group.id === workspaceRef);
  // Only an org admin may list every workspace, as the sidenav switcher does.
  const canSeeAllWorkspaces = usePermissions().hasAllPermissions(
    [ORG_PERMISSIONS.WORKSPACE.READ],
    "tenant"
  );
  const allGroups = useRemoteData(DATA_KEYS.ALL_GROUPS, undefined, {
    enabled:
      canSeeAllWorkspaces && UUID_RE.test(workspaceRef) && !!myGroups && !mine,
  }).data?.items;
  const workspace =
    mine ?? allGroups?.find((item) => item.group.id === workspaceRef);

  const noun = ENTITY[entity.kind].noun;
  const fallbackIcon = KIND_ICON[entity.kind] ?? null;
  if (workspace) {
    return { name: workspace.group.name, icon: fallbackIcon, href: null };
  }
  if (displayId) {
    return caseDisplay(caseItem, displayId, false);
  }
  if (!entity.id) {
    return { name: entity.name ?? noun, icon: fallbackIcon, href: null };
  }
  // The query that reads the entity, or what it lives in.
  const read: Partial<Record<EntityKind, { error: unknown }>> = {
    agent: agentQuery,
    knowledge_base: kbQuery,
    table: tableQuery,
    workflow: workflowQuery,
    conversation: conversationQuery,
    test_suite: suiteQuery,
    version: versionQuery,
    column: columnTableQuery,
    run: parentFlowQuery,
    test_case: parentSuiteQuery,
    evaluation_run: parentSuiteQuery,
  };
  // Deleted since the call ran: there is nothing left to open.
  if (notFound(read[entity.kind]?.error)) {
    return { name: entity.name ?? noun, icon: fallbackIcon, href: null };
  }
  switch (entity.kind) {
    case "agent":
      return {
        name: names.agentNameFor(id) ?? agent?.name ?? entity.name ?? noun,
        loading:
          !names.agentNameFor(id) && !entity.name && agentQuery.isLoading,
        icon: agent?.image_url || "coworker",
        href: getUrlForEntity("assistant", id),
      };
    case "knowledge_base":
      return {
        name: names.kbNameFor(id) ?? kb?.name ?? entity.name ?? noun,
        loading: !names.kbNameFor(id) && !entity.name && kbQuery.isLoading,
        icon: metaIcon(kb?.meta) ?? "kb",
        href: getUrlForEntity("kb", id),
      };
    case "table":
      return {
        name: table?.name ?? entity.name ?? noun,
        loading: !entity.name && tableQuery.isLoading,
        icon: "table",
        href: getUrlForEntity("table", id),
      };
    case "workflow":
      return {
        name: workflow?.name ?? entity.name ?? noun,
        loading: !entity.name && workflowQuery.isLoading,
        icon: metaIcon(workflow?.meta) ?? "workflow",
        href: getUrlForEntity("workflow", id),
      };
    case "conversation":
      return {
        name: conversation?.name ?? noun,
        loading: conversationQuery.isLoading,
        icon: conversationAgent?.image_url || "coworker",
        href: conversation?.assistant_id
          ? `/chat/${id}?assistant_id=${conversation.assistant_id}`
          : null,
      };
    case "version":
      return {
        name: versionQuery.data?.name ?? entity.name ?? noun,
        loading: !entity.name && versionQuery.isLoading,
        icon: null,
        href: parent ? `/assistant/${parent}?version=${id}` : null,
      };
    case "deployment": {
      const deployment = deploymentsQuery.data?.find((d) => d.id === id);
      return {
        name:
          deployment?.name ||
          (deployment ? humanize(deployment.channel_type) : undefined) ||
          entity.name ||
          noun,
        loading: !entity.name && deploymentsQuery.isLoading,
        icon: null,
        href: parent
          ? `/assistant/${parent}?tab=app&deployment_id=${id}`
          : null,
      };
    }
    case "column": {
      // A column is named by its SQL name; it reads by its label.
      const column = columnTableQuery.data?.columns.find((c) => c.name === id);
      return {
        name: column?.label || entity.name || id,
        loading: !entity.name && columnTableQuery.isLoading,
        icon: null,
        href: parent ? getUrlForEntity("table", parent) : null,
      };
    }
    case "run":
      return {
        name: parentFlow?.name ? `${parentFlow.name} run` : noun,
        icon: null,
        href: parent ? `/flows/${parent}?tab=flow&preview_run=${id}` : null,
      };
    case "test_case":
      return {
        name: entity.name ?? noun,
        icon: null,
        href: parentSuite?.agent_id
          ? `/assistant/${parentSuite.agent_id}?tab=tests&set=${parent}&test=${id}`
          : null,
      };
    case "evaluation_run":
      return {
        name: parentSuite?.name ? `${parentSuite.name} run` : noun,
        icon: null,
        href: parentSuite?.agent_id
          ? `/assistant/${parentSuite.agent_id}?tab=tests&set=${parent}&run=${id}`
          : null,
      };
    case "sync": {
      // A sync reads by what it watches, as the Syncs tab shows it.
      const sync = syncsQuery.data?.find((s) => s.trigger_id === id);
      return {
        name: sync ? formatSyncResourceLabel(sync) : (entity.name ?? noun),
        loading: !sync && !entity.name && syncsQuery.isLoading,
        icon: null,
        href: parent ? getUrlForEntity("kb", parent) : null,
      };
    }
    case "test_suite":
      return {
        name: suite?.name ?? noun,
        loading: suiteQuery.isLoading,
        icon: "flask",
        href: suite?.agent_id
          ? `/assistant/${suite.agent_id}?tab=tests&set=${id}`
          : null,
      };
    case "inbox":
      return {
        name: inbox?.name ?? entity.name ?? noun,
        loading: !entity.name && inboxQuery.isLoading,
        icon: metaIcon(inbox?.meta) ?? "inbox",
        href: `/inbox/${id}`,
      };
    case "case":
      return caseDisplay(
        caseItem,
        entity.name ?? noun,
        !entity.name && caseQuery.isLoading
      );
    case "usage_limit": {
      const limit = limitsQuery.data?.limits.find((item) => item.id === id);
      return {
        name: limit
          ? usageLimitName(
              limit,
              billingCurrency ? providerCurrency(billingCurrency) : null
            )
          : noun,
        loading: limitsQuery.isLoading,
        icon: null,
        href: "/settings/administration/billing?tab=limits",
      };
    }
    default:
      return { name: entity.name ?? noun, icon: fallbackIcon, href: null };
  }
}

// A limit as Usage controls lists it: what it counts, where, and its cap. A
// spend cap waits for the billing currency rather than show the wrong one.
function usageLimitName(
  limit: OperationsLimit | ProviderCostLimit,
  money: ReturnType<typeof providerCurrency> | null
): string {
  const spend = limit.metric === "provider";
  const counts = spend
    ? `Model spend${limit.provider ? ` on ${limit.provider.name}` : ""}`
    : "AI operations";
  const where = limit.group?.name ?? "the whole organization";
  if (spend && limit.limit != null && !money) return `${counts} in ${where}`;
  const cap =
    limit.limit == null
      ? "no cap"
      : `${spend && money ? money.format(limit.limit) : limit.limit.toLocaleString()} a month`;
  return `${counts} in ${where}, ${cap}`;
}

// A refetch of an entity that is gone starts over as pending: retrying the
// 404 would show its link again until the retries run out.
const retryUnlessGone = (failures: number, error: unknown) =>
  !notFound(error) && failures < 2;

function notFound(error: unknown): boolean {
  return isAxiosError(error) && error.response?.status === 404;
}

// A case reads as its display id and subject, as the case page heads it.
function caseDisplay(
  caseItem:
    | { id: string; display_id?: string | null; subject: string }
    | undefined,
  fallback: string,
  loading: boolean
): EntityDisplay {
  if (!caseItem) return { name: fallback, icon: null, href: null, loading };
  return {
    name: caseItem.display_id
      ? `${caseItem.display_id} · ${caseItem.subject}`
      : caseItem.subject,
    icon: null,
    href: `/cases/${caseItem.id}`,
  };
}

function metaIcon(meta: unknown): string | null {
  return isRecord(meta) && typeof meta.icon === "string" && meta.icon
    ? meta.icon
    : null;
}

function EntityIcon({ kind, icon }: { kind: EntityKind; icon: string | null }) {
  return icon ? (
    <NoxusIcon icon={icon} size="xsm" />
  ) : (
    <EntityTile kind={kind} />
  );
}

// An entity that exists: its own icon and name, opening its page.
export function EntityPill({
  entity,
  struck = false,
  strong = false,
  linked = true,
}: {
  entity: EntityRef;
  struck?: boolean;
  strong?: boolean;
  linked?: boolean;
}) {
  const { name, icon, href, loading } = useEntityDisplay(entity);
  const label = loading ? (
    <Skeleton
      role="status"
      className="h-3 w-16"
      aria-label={`Loading ${name}`}
    />
  ) : (
    <span
      data-label
      className={cn(
        strong && "font-medium",
        struck && "line-through decoration-red-400/70"
      )}
    >
      {name}
    </span>
  );
  const content = (
    <>
      <EntityIcon kind={entity.kind} icon={icon} />
      {label}
    </>
  );
  if (!href || !linked) {
    return (
      <Badge variant="outline" size="sm" ellipsis className="gap-1.5 pr-1.5">
        {content}
      </Badge>
    );
  }
  return (
    <Badge
      variant="outline"
      size="sm"
      ellipsis
      asChild
      className="cursor-pointer gap-1.5 pr-1.5 hover:bg-muted"
    >
      <Link href={href} title={`Open ${name}`}>
        {content}
      </Link>
    </Badge>
  );
}

// A tool card's header: what the call changed, opening it.
export function EntityCardHeader({ entity }: { entity: EntityRef }) {
  const { name, icon, href, loading } = useEntityDisplay(entity);
  if (loading)
    return (
      <div className="flex items-center gap-2 border-b border-border px-3 py-2 last:border-b-0">
        <Skeleton
          role="status"
          className="h-3 w-24"
          aria-label={`Loading ${name}`}
        />
      </div>
    );
  if (!href) return null;
  return (
    <Link
      href={href}
      aria-label={`Open ${name}`}
      title={`Open ${name}`}
      className="flex min-w-0 items-center gap-2 border-b border-border px-3 py-2 text-xs text-foreground last:border-b-0 hover:bg-muted"
    >
      <EntityIcon kind={entity.kind} icon={icon} />
      <span className="min-w-0 flex-1 truncate font-medium">{name}</span>
      <ArrowUpRight className="size-3.5 shrink-0 text-muted-foreground" />
    </Link>
  );
}

// A flow step named by a handle (its id, name or label) reads by its name;
// an id the flow does not hold reads as a step, never as the id.
function FlowStepName({
  workflowId,
  handle,
}: {
  workflowId: string | undefined;
  handle: string;
}) {
  const node = findNodeByHandle(useApprovalFlowNodes(workflowId), handle);
  return <>{node?.name || (UUID_RE.test(handle) ? "Step" : handle)}</>;
}

// A knowledge base sync reads by what it watches, as the Syncs tab shows it;
// one the knowledge base does not hold reads as a sync, never as the id.
function SyncName({ kbId, syncId }: { kbId: string; syncId: string }) {
  const sync = useKBSyncList(kbId).data?.find((s) => s.trigger_id === syncId);
  if (!sync) return <>Sync</>;
  return (
    <span className="inline-flex min-w-0 items-center gap-1">
      <SourceIcon sourceType={sync.source_type} size={14} />
      <span className="truncate">{formatSyncResourceLabel(sync)}</span>
    </span>
  );
}

// The struck "from" side of a rename reads the current name as plain text.
function EntityNameText({ entity }: { entity: EntityRef }) {
  return <>{useEntityDisplay(entity).name}</>;
}

// Whether a row sits straight in a FieldList, whose grid lines every label up
// in one column.
const InFieldList = createContext(false);

// The labelled rows of a card step. Labels share one column, as wide as the
// widest up to a cap; anything else spans the step.
export function FieldList({
  className,
  children,
}: {
  className?: string;
  children: ReactNode;
}) {
  return (
    <InFieldList.Provider value={true}>
      <div
        data-slot="field-list"
        className={cn(
          "grid grid-cols-[fit-content(9rem)_minmax(0,1fr)] items-start gap-x-2 gap-y-1.5 *:col-span-2",
          className
        )}
      >
        {children}
      </div>
    </InFieldList.Provider>
  );
}

// A label and its value. In a FieldList the two are its grid's cells (the
// wrapper has no box, so its span does not apply); anywhere else, a row.
export function LabeledRow({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) {
  const inList = useContext(InFieldList);
  return (
    <div className={inList ? "contents" : "flex items-start gap-2"}>
      <span
        data-slot="field-label"
        className={cn(
          "pt-px text-muted-foreground",
          !inList && "min-w-[4.5rem] shrink-0"
        )}
      >
        {label}
      </span>
      <div className={cn("min-w-0", !inList && "flex-1")}>
        <InFieldList.Provider value={false}>{children}</InFieldList.Provider>
      </div>
    </div>
  );
}

// A labelled one-line value. ``from`` is what it replaces, struck through.
export function InlineRow({
  label,
  edit,
  from,
  whole = false,
  children,
}: {
  label: string;
  edit: boolean;
  from?: ReactNode;
  // Long text the user must read whole before approving (a description).
  whole?: boolean;
  children: ReactNode;
}) {
  return (
    <LabeledRow label={label}>
      <span className="flex min-w-0 flex-1 flex-wrap items-baseline gap-x-1">
        {from !== undefined && (
          <>
            <span className="min-w-0 break-words font-mono text-[11px] text-red-400/80 line-through">
              {from}
            </span>
            <span className="shrink-0 text-muted-foreground">→</span>
          </>
        )}
        <span
          className={cn(
            "min-w-0 break-words",
            !whole && "line-clamp-3",
            edit
              ? "font-mono text-[11px] text-emerald-600"
              : "text-foreground/80"
          )}
        >
          {children}
        </span>
      </span>
    </LabeledRow>
  );
}

// A value too big for a label row (code, a message, a row preview) takes the
// step's full width.
function BlockRow({
  label,
  children,
}: {
  label?: string;
  children: ReactNode;
}) {
  return (
    <InFieldList.Provider value={false}>
      {label ? (
        <div className="space-y-1">
          <span className="text-muted-foreground">{label}</span>
          {children}
        </div>
      ) : (
        <div>{children}</div>
      )}
    </InFieldList.Provider>
  );
}

// The ids a column's cells hold, shared by each reference cell's lookup.
function columnIds(rows: string[][], index: number): string[] {
  return rows.map((row) => row[index]).filter((cell) => !!cell);
}

// One cell: a file reads as the file, a reference as the row it links to, an
// entity id as the entity's name, anything else as text cut short.
function PreviewCell({
  cell,
  refTable,
  refIds,
  entityKind,
}: {
  cell: string;
  refTable?: string;
  refIds?: string[];
  entityKind?: EntityKind;
}) {
  if (entityKind)
    return <EntityNameText entity={{ kind: entityKind, id: cell }} />;
  if (refTable)
    return (
      <ReferenceValue tableId={refTable} id={cell} ids={refIds ?? [cell]} />
    );
  return spotFileId(cell) ? (
    <FileCell uri={cell} />
  ) : (
    <span title={cell} className="line-clamp-2 break-words">
      {cell}
    </span>
  );
}

// Two columns (a schema: name and type) still fit side by side; changes to
// existing rows never do, since each needs the row it changes as a heading.
function PreviewGrid({
  columns,
  rows,
  refTables,
  entityKinds,
}: {
  columns: string[];
  rows: string[][];
  refTables?: (string | undefined)[];
  entityKinds?: (EntityKind | undefined)[];
}) {
  return (
    <div className="overflow-hidden rounded-md border">
      <Table className="table-fixed text-xs">
        <TableHeader className="bg-muted/50">
          <TableRow>
            {columns.map((c) => (
              <TableHead key={c} className="h-6 px-2 py-0 text-xs">
                {c}
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((row, i) => (
            <TableRow key={i} className="hover:bg-transparent">
              {row.map((cell, j) => (
                <TableCell key={j} className="px-2 py-1">
                  <PreviewCell
                    cell={cell}
                    refTable={refTables?.[j]}
                    refIds={columnIds(rows, j)}
                    entityKind={entityKinds?.[j]}
                  />
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

// More columns than a narrow card can hold side by side: one block per row,
// headed by its name, with a line per value. With ``rowIds`` the call changes
// those rows: ``titles`` heads them by their current name and their lines are
// every value it sets.
function PreviewRecords({
  columns,
  rows,
  cleared,
  rowIds,
  titles,
  refTables,
  entityKinds,
  before,
}: {
  columns: string[];
  rows: string[][];
  cleared?: boolean[][];
  rowIds?: string[];
  titles?: (string | undefined)[];
  refTables?: (string | undefined)[];
  entityKinds?: (EntityKind | undefined)[];
  before?: (string | undefined)[][];
}) {
  const t = titleColumn(columns);
  const changes = !!rowIds;
  return (
    <div className="divide-y rounded-md border">
      {rows.map((row, i) => {
        // A change is headed by the row it changes, never by a value it sets.
        const heading = changes ? titles?.[i] : row[t];
        const lines = columns
          .map((column, index) => ({
            column,
            index,
            cell: row[index],
            isCleared: !!cleared?.[i]?.[index],
          }))
          .filter(
            ({ cell, isCleared, index }) =>
              (cell || (changes && isCleared)) && (changes || index !== t)
          );
        return (
          <div key={rowIds?.[i] ?? i} className="space-y-0.5 px-2 py-1.5">
            <div className="truncate font-medium text-foreground">
              {heading || `Row ${i + 1}`}
            </div>
            {lines.map(({ column, index, cell, isCleared }) => {
              const was = before?.[i]?.[index];
              const replaced = !!was && was !== cell;
              return (
                <div key={index} className="flex min-w-0 gap-2">
                  <span
                    title={column}
                    className="w-20 shrink-0 truncate text-muted-foreground"
                  >
                    {column}
                  </span>
                  <span className="flex min-w-0 flex-1 flex-wrap items-baseline gap-x-1 text-foreground/80">
                    {replaced && (
                      <>
                        <span className="min-w-0 break-words text-red-400/80 line-through">
                          {was}
                        </span>
                        <span className="shrink-0 text-muted-foreground">
                          →
                        </span>
                      </>
                    )}
                    {cell ? (
                      <PreviewCell
                        cell={cell}
                        refTable={refTables?.[index]}
                        refIds={columnIds(rows, index)}
                        entityKind={entityKinds?.[index]}
                      />
                    ) : (
                      isCleared && (
                        <span className="italic text-muted-foreground">
                          Cleared
                        </span>
                      )
                    )}
                  </span>
                </div>
              );
            })}
          </div>
        );
      })}
    </div>
  );
}

// The first rows, the rest one click away: the user approves what they can see.
function PreviewRows({
  table,
  titles,
}: {
  table: RowsPreview;
  titles?: (string | undefined)[];
}) {
  const [expanded, setExpanded] = useState(false);
  // Expanded, a preview lists as many rows as a lookup reads, so every row a
  // change names is headed by its name; the rest are counted.
  const rows = table.rows.slice(0, expanded ? LOOKUP_ROWS : PREVIEW_ROWS);
  const hidden = expanded
    ? 0
    : Math.min(table.rows.length, LOOKUP_ROWS) - rows.length;
  const unread = table.total - rows.length;
  const grid = table.columns.length <= 2 && !table.rowIds;
  return (
    <div className="space-y-1">
      {!grid && table.total > 1 && (
        <p className="text-muted-foreground">{plural(table.total, "row")}</p>
      )}
      {grid ? (
        <PreviewGrid
          columns={table.columns}
          rows={rows}
          refTables={table.refTables}
          entityKinds={table.entityKinds}
        />
      ) : (
        <PreviewRecords
          columns={table.columns}
          rows={rows}
          cleared={table.cleared}
          rowIds={table.rowIds}
          titles={titles}
          refTables={table.refTables}
          entityKinds={table.entityKinds}
          before={table.before}
        />
      )}
      {hidden > 0 ? (
        <Button
          variant="link"
          size="sm"
          className="h-auto p-0 text-xs"
          onClick={() => setExpanded(true)}
        >
          Show {hidden} more
        </Button>
      ) : (
        unread > 0 && <p className="text-muted-foreground">and {unread} more</p>
      )}
    </div>
  );
}

// How many cases go with an inbox, or with every case of it. Read live like
// a table's rows, so a recap after the delete shows none.
function InboxCaseCount({ inboxId }: { inboxId: string }) {
  const { spotGroup } = useSpotUser();
  const g = spotGroup.id;
  const total = useRemoteData(
    DATA_KEYS.CASES,
    { path: { group_id: g }, query: { inbox_id: inboxId, page: 1, size: 1 } },
    { enabled: !!g }
  ).data?.total;
  return total ? (
    <span className="text-muted-foreground">{plural(total, "case")}</span>
  ) : null;
}

function EntityCount({ kind, id }: { kind: EntityKind; id: string }) {
  if (kind === "table") return <TableRowCount tableId={id} />;
  if (kind === "inbox") return <InboxCaseCount inboxId={id} />;
  return null;
}

// How many rows a card that deletes them is about to remove. The count is
// read live, so once a clear has run its recap would read 0: none is shown.
function TableRowCount({ tableId }: { tableId: string }) {
  const { spotGroup } = useSpotUser();
  const g = spotGroup.id;
  const table = useRemoteData(
    DATA_KEYS.TABLES_ID,
    { path: { group_id: g, table_id: tableId } },
    { enabled: !!g }
  ).data;
  return table && table.row_count > 0 ? (
    <span className="text-muted-foreground">
      {plural(table.row_count, "row")}
    </span>
  ) : null;
}

function KnowledgeBaseDocumentCount({ kbId }: { kbId: string }) {
  const { spotGroup } = useSpotUser();
  const g = spotGroup.id;
  const kb = useRemoteData(
    DATA_KEYS.KNOWLEDGE_BASES_V2_ID,
    { path: { group_id: g, knowledge_base_id: kbId } },
    { enabled: !!g }
  ).data;
  return kb && kb.num_docs > 0 ? (
    <span className="text-muted-foreground">
      {plural(kb.num_docs, "document")}
    </span>
  ) : null;
}

function useTableRows(tableId: string, rowIds: (string | number)[]) {
  const { spotGroup } = useSpotUser();
  const g = spotGroup.id;
  // An id that cannot be a row's (mistyped by the model) would fail the whole
  // lookup and leave every row unnamed; that row just reads "Row n".
  const ids = rowIds
    .map(String)
    .filter((id) => UUID_RE.test(id) || /^\d+$/.test(id))
    .slice(0, LOOKUP_ROWS);
  const table = useRemoteData(
    DATA_KEYS.TABLES_ID,
    { path: { group_id: g, table_id: tableId } },
    { enabled: !!g }
  ).data;
  const rows = useRemoteData(
    DATA_KEYS.TABLE_ROWS,
    {
      path: { group_id: g, table_id: tableId },
      query: {
        limit: LOOKUP_ROWS,
        filters: JSON.stringify([{ field: "id", op: "in", value: ids }]),
      },
    },
    { enabled: !!g && ids.length > 0 }
  ).data;
  return { table, rows: rows?.rows };
}

// Rows the call names by id, read from their table so the card shows what
// they hold. Rows already gone (an approved deletion) show nothing.
function LookedUpRows({
  tableId,
  rowIds,
}: {
  tableId: string;
  rowIds: (string | number)[];
}) {
  const { table, rows } = useTableRows(tableId, rowIds);
  const preview =
    table && rows ? lookedUpRows(table.columns, rows, rowIds.length) : null;
  return preview ? <PreviewRows table={preview} /> : null;
}

// Changes to existing rows, each headed by the row it changes.
function ChangedRows({
  preview,
  tableId,
}: {
  preview: RowsPreview;
  tableId: string;
}) {
  const rowIds = preview.rowIds ?? [];
  const { table, rows } = useTableRows(tableId, rowIds);
  const column =
    table?.columns[titleColumn(table.columns.map((c) => c.label || c.name))];
  const rowFor = (id: string) => rows?.find((r) => String(r.id) === id);
  const titles =
    column && rows
      ? rowIds.map((id) => {
          const row = rowFor(id);
          return row ? formatValue(row[column.name]) : undefined;
        })
      : undefined;
  const before = rows
    ? rowIds.map((id) => {
        const row = rowFor(id);
        return preview.columns.map((name) =>
          row ? formatValue(row[name]) : undefined
        );
      })
    : undefined;
  const withBefore = {
    ...preview,
    before: useContext(Stage) === "pending" ? before : undefined,
  };
  return (
    <PreviewRows
      table={table ? withTableColumns(withBefore, table.columns) : withBefore}
      titles={titles}
    />
  );
}

// New rows for a table the card can read: headed by the table's column
// labels, with each reference as the row it links to.
function InsertedRows({
  preview,
  tableId,
}: {
  preview: RowsPreview;
  tableId: string;
}) {
  const { spotGroup } = useSpotUser();
  const table = useRemoteData(
    DATA_KEYS.TABLES_ID,
    { path: { group_id: spotGroup.id, table_id: tableId } },
    { enabled: !!spotGroup.id }
  ).data;
  return (
    <PreviewRows
      table={table ? withTableColumns(preview, table.columns) : preview}
    />
  );
}

// What a SQL write does, before the SQL itself: its verb, target table and,
// for an UPDATE, how many rows it changes, read by the server's SQL parser.
function StatementSummary({ sql }: { sql: string }) {
  const { spotGroup } = useSpotUser();
  const summary = useRemoteData(
    DATA_KEYS.TABLES_WRITE_SUMMARY,
    { path: { group_id: spotGroup.id }, body: { sql } },
    { enabled: !!spotGroup.id, retry: false }
  ).data;
  if (!summary) return null;
  return (
    <InlineRow
      label={summary.kind === "insert" ? "Inserts into" : "Updates"}
      edit={false}
    >
      {summary.table_id ? (
        <EntityPill entity={{ kind: "table", id: summary.table_id }} />
      ) : (
        <span className="font-mono text-[11px]">{summary.sql_name}</span>
      )}
      {typeof summary.affected === "number" && (
        <span className="text-muted-foreground">
          {plural(summary.affected, "row")}
        </span>
      )}
    </InlineRow>
  );
}

const optionalString = (v: unknown) => v === undefined || typeof v === "string";
const absent = (v: unknown) => v === undefined || v === null;
const isStringList = (v: unknown) =>
  Array.isArray(v) && v.every((x) => typeof x === "string");

const CODE_LANGUAGES: Record<CodeLanguage, true> = {
  sql: true,
  bash: true,
  python: true,
  javascript: true,
  tsx: true,
  json: true,
  yaml: true,
  html: true,
  css: true,
  markdown: true,
};

// Every property the card reads off a field's display, checked against what
// it reads it as: a server-sent display that would break the render is
// refused here instead.
function isArgField(f: unknown): boolean {
  if (
    !isRecord(f) ||
    typeof f.arg !== "string" ||
    typeof f.type !== "string" ||
    !optionalString(f.label)
  ) {
    return false;
  }
  switch (f.type) {
    case "entity_ref":
      return (
        typeof f.kind === "string" &&
        f.kind in ENTITY &&
        (absent(f.show_rows) || typeof f.show_rows === "boolean") &&
        (absent(f.workflow_arg) || typeof f.workflow_arg === "string") &&
        (absent(f.parent_arg) || typeof f.parent_arg === "string")
      );
    case "select":
      return absent(f.values) || Array.isArray(f.values);
    case "code_view":
      return (
        (absent(f.language) ||
          (typeof f.language === "string" && f.language in CODE_LANGUAGES)) &&
        (absent(f.path_field) || typeof f.path_field === "string")
      );
    case "rows_lookup":
      return typeof f.table_arg === "string";
    case "rows_preview":
      return (
        (absent(f.values_key) || typeof f.values_key === "string") &&
        (absent(f.row_key) || typeof f.row_key === "string") &&
        (absent(f.table_arg) || typeof f.table_arg === "string") &&
        (absent(f.columns) ||
          (Array.isArray(f.columns) &&
            f.columns.every(
              (c) =>
                Array.isArray(c) &&
                c.length === 2 &&
                typeof c[0] === "string" &&
                isStringList(c[1])
            ))) &&
        (absent(f.labels) ||
          (isRecord(f.labels) &&
            Object.values(f.labels).every(
              (m) =>
                isRecord(m) &&
                Object.values(m).every((l) => typeof l === "string")
            ))) &&
        (absent(f.entities) ||
          (isRecord(f.entities) &&
            Object.values(f.entities).every(
              (k) => typeof k === "string" && k in ENTITY
            )))
      );
    default:
      return true;
  }
}

// A declaration as stamped on the call. Any server can send one, so anything
// the card cannot draw (an unknown op or kind, malformed fields) falls back to
// the plain args card instead of breaking it.
export function readToolApproval(raw: unknown): ToolApproval | null {
  if (!isRecord(raw)) return null;
  const valid =
    (raw.op === undefined ||
      (typeof raw.op === "string" && raw.op in OP_ICON)) &&
    (raw.entity === undefined ||
      (typeof raw.entity === "string" && raw.entity in ENTITY)) &&
    (raw.fields === undefined ||
      (Array.isArray(raw.fields) && raw.fields.every(isArgField))) &&
    (raw.results === undefined ||
      (Array.isArray(raw.results) && raw.results.every(isArgField))) &&
    (raw.renderer === undefined ||
      APPROVAL_RENDERERS.some((r) => r === raw.renderer)) &&
    (raw.replaces === undefined ||
      (isRecord(raw.replaces) &&
        Object.values(raw.replaces).every((v) => typeof v === "string"))) &&
    [
      "id_arg",
      "name_arg",
      "label_arg",
      "count_arg",
      "rename_arg",
      "parent_arg",
      "warning",
    ].every((k) => optionalString(raw[k]));
  return valid ? (raw as ToolApproval) : null;
}

const isText = (value: unknown): value is string => typeof value === "string";

// An uploaded file is named by its bare id: it reads as the file.
const fileUri = (id: string) =>
  id.startsWith("spot://") ? id : `spot://${id}`;

// One arg, read through its NCL display. Read-only: the card shows what the
// call will do, it never edits it.
function FieldValue({
  name,
  display,
  values,
  edit,
  replaced,
}: {
  name: string;
  display: FieldDisplay;
  values: Record<string, unknown>;
  edit: boolean;
  // What the entity holds now for this arg, when the call replaces it.
  replaced?: unknown;
}) {
  const value =
    isEmptyValue(values[name]) && display.type === "text"
      ? display.placeholder
      : values[name];
  const label = display.label ?? "";
  const pending = useContext(Stage) === "pending";
  switch (display.type) {
    case "entity_ref":
      if (display.kind === "sync" && display.parent_arg) {
        const kbId = values[display.parent_arg];
        return typeof value === "string" && typeof kbId === "string" ? (
          <InlineRow label={label} edit={false}>
            <SyncName kbId={kbId} syncId={value} />
          </InlineRow>
        ) : null;
      }
      if (display.kind === "node" && display.workflow_arg) {
        const workflowId = values[display.workflow_arg];
        return typeof value === "string" ? (
          <InlineRow label={label} edit={false}>
            <FlowStepName
              workflowId={
                typeof workflowId === "string" ? workflowId : undefined
              }
              handle={value}
            />
          </InlineRow>
        ) : null;
      }
      if (display.kind === "file" && Array.isArray(value)) {
        return (
          <InlineRow label={label} edit={false}>
            <span className="flex min-w-0 flex-col gap-1">
              {value.filter(isText).map((id) => (
                <FileCell key={id} uri={fileUri(id)} typeIcon />
              ))}
            </span>
          </InlineRow>
        );
      }
      if (typeof value !== "string") return null;
      if (display.kind === "file") {
        const uri = fileUri(value);
        return (
          <InlineRow label={label} edit={false}>
            {spotFileId(uri) ? <FileCell uri={uri} /> : value}
          </InlineRow>
        );
      }
      return (
        <InlineRow label={label} edit={false}>
          <EntityPill
            entity={{
              kind: display.kind,
              id: value,
              parent: textValue(values, display.parent_arg),
            }}
          />
          {display.show_rows && pending && (
            <EntityCount kind={display.kind} id={value} />
          )}
        </InlineRow>
      );
    case "toggle":
      return (
        <InlineRow
          label={label}
          edit={edit}
          from={
            typeof replaced === "boolean" && replaced !== value
              ? yesNo(replaced)
              : undefined
          }
        >
          {yesNo(value === true)}
        </InlineRow>
      );
    case "model_select":
      return (
        <InlineRow label={label} edit={edit}>
          <ModelSelectCompact
            value={value}
            display={{ endpoint: display.endpoint }}
          />
        </InlineRow>
      );
    case "select": {
      // A list arg (a role's permissions) reads as each choice's label.
      if (Array.isArray(value))
        return (
          <InlineRow label={label} edit={false} whole>
            {value.map((v) => choiceLabel(display.values, v)).join(", ")}
          </InlineRow>
        );
      const was =
        replaced === undefined || replaced === value
          ? undefined
          : choiceLabel(display.values, replaced);
      const description = choiceDescription(display.values, value);
      return (
        <>
          <InlineRow label={label} edit={edit} from={was}>
            {choiceLabel(display.values, value)}
          </InlineRow>
          {description && (
            <p className="text-muted-foreground">{description}</p>
          )}
        </>
      );
    }
    case "code_view":
      return typeof value === "string" ? (
        <BlockRow label={label}>
          {display.language === "sql" && <StatementSummary sql={value} />}
          <CodeBlock
            code={value}
            language={codeLanguage(display, values)}
            className="max-h-40 overflow-auto [&_code]:text-[11px]! [&_pre]:p-2! [&_pre]:text-[11px]! [&_pre]:whitespace-pre-wrap"
          />
        </BlockRow>
      ) : null;
    case "quote": {
      const text = quoteText(value);
      return text ? (
        <BlockRow label={label}>
          <p className="whitespace-pre-wrap rounded-md bg-muted px-2 py-1.5 text-foreground/80 line-clamp-6">
            {text}
          </p>
        </BlockRow>
      ) : null;
    }
    case "schedule": {
      const when = scheduleText(value);
      const was =
        isRecord(replaced) && JSON.stringify(replaced) !== JSON.stringify(value)
          ? (scheduleText(replaced) ?? undefined)
          : undefined;
      return (
        <InlineRow label={label} edit={edit} from={was}>
          {when ?? <EntryValue value={value} />}
        </InlineRow>
      );
    }
    case "rows_lookup": {
      const tableId = values[display.table_arg];
      return typeof tableId === "string" && Array.isArray(value) ? (
        <BlockRow label={label}>
          <LookedUpRows tableId={tableId} rowIds={value} />
        </BlockRow>
      ) : null;
    }
    case "rows_preview": {
      const table = previewRows(display, value);
      const tableId = display.table_arg ? values[display.table_arg] : null;
      return table ? (
        <BlockRow label={label}>
          {typeof tableId === "string" && table.rowIds ? (
            <ChangedRows preview={table} tableId={tableId} />
          ) : typeof tableId === "string" ? (
            <InsertedRows preview={table} tableId={tableId} />
          ) : (
            <PreviewRows table={table} />
          )}
        </BlockRow>
      ) : null;
    }
    default:
      // A text field with no label spreads a dict into one row per entry.
      if (!label && isRecord(value)) {
        return (
          <>
            {Object.entries(value).map(([k, v]) =>
              isEmptyValue(v) ? null : (
                <InlineRow key={k} label={humanize(k)} edit={edit}>
                  <EntryValue value={v} />
                </InlineRow>
              )
            )}
          </>
        );
      }
      return (
        <InlineRow
          label={label}
          edit={edit}
          whole={display.type === "text" && !!display.is_textarea}
          from={
            isScalar(replaced) && replaced !== value
              ? cellText(replaced)
              : undefined
          }
        >
          <EntryValue value={value} />
        </InlineRow>
      );
  }
}

function yesNo(on: boolean): string {
  return on ? "Yes" : "No";
}

function isScalar(value: unknown): value is string | number | boolean {
  return ["string", "number", "boolean"].includes(typeof value);
}

// A schedule as the trigger card says it, in the viewer's time zone.
function scheduleText(value: unknown): string | null {
  return isRecord(value)
    ? describeSchedule(
        { ...value, type: "schedule_v2" },
        Intl.DateTimeFormat().resolvedOptions().timeZone
      )
    : null;
}

function textValue(
  values: Record<string, unknown>,
  key: string | null | undefined
): string | undefined {
  const value = key ? values[key] : undefined;
  return typeof value === "string" && value ? value : undefined;
}

// A usage limit's args read as the limits page shows them: its provider by
// name, and its cap in the billing currency, or as AI operations.
function useShownValues(
  entity: EntityRef | undefined,
  values: Record<string, unknown>
): Record<string, unknown> {
  const on = entity?.kind === "usage_limit";
  const providers = useRemoteData(
    DATA_KEYS.BILLING_LIMIT_PROVIDERS,
    undefined,
    { enabled: on }
  ).data;
  const limits = useRemoteData(
    DATA_KEYS.BILLING_LIMITS,
    {},
    { enabled: on && !!entity?.id }
  ).data;
  const currency = useRemoteData(DATA_KEYS.BILLING_CURRENCY, undefined, {
    enabled: on,
  }).data;
  if (!on) return values;
  const provider = providers?.find((item) => item.id === values.provider_id);
  const limit = limits?.limits.find((item) => item.id === entity?.id);
  const spend = values.counts === "spend" || limit?.metric === "provider";
  const amount = values.amount;
  return {
    ...values,
    ...(provider && { provider_id: provider.name }),
    // Until the billing currency loads, the cap shows as the bare amount.
    ...(typeof amount === "number" &&
      (!spend || currency) && {
        amount: spend
          ? formatMoney(amount, providerCurrency(currency).currency)
          : `${amount.toLocaleString("en-US")} AI operations`,
      }),
  };
}

// What an entity holds now, for a card that shows the values a call replaces.
// Only a column says yet: it is read from its table.
// What an entity holds now, for a card that shows the values a call replaces:
// a column from its table, a sync from its knowledge base's syncs, a knowledge
// base's settings from the knowledge base, a member (named by email) from its
// workspace's members, a workspace from itself.
function useCurrentRecord(
  entity: EntityRef | undefined,
  enabled: boolean
): Record<string, unknown> | undefined {
  const { spotGroup } = useSpotUser();
  const g = spotGroup.id;
  const on = enabled && !!g && !!entity?.id;
  const kind = enabled ? entity?.kind : undefined;
  const tableId =
    on && kind === "column" && entity?.parent ? entity.parent : "";
  const table = useRemoteData(
    DATA_KEYS.TABLES_ID,
    { path: { group_id: g, table_id: tableId } },
    { enabled: !!tableId }
  ).data;
  const syncKb = on && kind === "sync" && entity?.parent ? entity.parent : "";
  const syncs = useKBSyncList(syncKb, !!syncKb).data;
  const kbId = on && kind === "knowledge_base" ? (entity?.id ?? "") : "";
  const kb = useRemoteData(
    DATA_KEYS.KNOWLEDGE_BASES_V2_ID,
    { path: { group_id: g, knowledge_base_id: kbId } },
    { enabled: !!kbId }
  ).data;
  const email = kind === "member" && entity?.parent ? entity.name : undefined;
  const members = useRemoteData(
    DATA_KEYS.GROUP_USERS,
    { path: { group_id: entity?.parent ?? "" }, query: { email } },
    { enabled: !!email, retry: false }
  ).data?.items;
  const workspaceId = kind === "workspace" ? (entity?.id ?? "") : "";
  const group = useRemoteData(
    DATA_KEYS.GROUP,
    { path: { group_id: workspaceId } },
    { enabled: UUID_RE.test(workspaceId), retry: false }
  ).data?.group;
  if (tableId) return table?.columns.find((c) => c.name === entity?.id);
  if (syncKb) return syncs?.find((s) => s.trigger_id === entity?.id);
  if (kbId && isRecord(kb?.settings_)) return kb.settings_;
  if (email) {
    const member = members?.find(
      (m) => m.user.email.toLowerCase() === email.toLowerCase()
    );
    // role_info names the role as the Members tab shows it ("Editor");
    // role is the legacy lowercase value.
    return member && { role: member.user.role_info?.name ?? member.role };
  }
  if (workspaceId && group) {
    return {
      name: group.name,
      description: group.description,
      // A workspace with no colour saved shows the first swatch, as its
      // General tab does.
      color: group.settings?.color || COLOUR_TOKENS[0].id,
      editor_v2: group.settings?.editor_v2_enabled ? "on" : "off",
      case_management: group.settings?.case_management_enabled ? "on" : "off",
    };
  }
  return undefined;
}

// Rows with no ids of their own (a clear) are the rows of their table: the
// heading is that table.
function headedByParent(entity: EntityRef | undefined): entity is EntityRef & {
  parent: string;
} {
  return (
    entity?.kind === "rows" &&
    !!entity.parent &&
    !entity.id &&
    entity.count === undefined
  );
}

// A value on a labelled row: a file reads as the file, anything else as text.
function EntryValue({ value }: { value: unknown }) {
  const text = cellText(value);
  if (text && spotFileId(text)) return <FileCell uri={text} />;
  return <>{text}</>;
}

// An arg the call did not pass renders nothing, and neither does a toggle
// left off: the card lists what will happen. A text field's placeholder says
// what leaving it out does, so it shows in its place.
function isShown(
  display: FieldDisplay,
  value: unknown,
  replaced?: unknown
): boolean {
  // A toggle turned off shows only where it was on.
  if (display.type === "toggle")
    return value === true || (value === false && replaced === true);
  return (
    !isEmptyValue(value) || (display.type === "text" && !!display.placeholder)
  );
}

// A row about an entity that exists heads with its pill; one being created
// has no page yet, so it heads with its icon and name.
export function HeadEntity({ entity, op }: { entity: EntityRef; op: StepOp }) {
  const struck = op === "remove";
  const stage = useContext(Stage);
  // Only a delete that ran took the entity away; a denied one left it.
  const gone = struck && stage === "ran";
  // The rows go, the table stays: it is not struck.
  if (headedByParent(entity)) {
    return (
      <>
        <EntityPill entity={{ kind: "table", id: entity.parent }} strong />
        {stage === "pending" && <TableRowCount tableId={entity.parent} />}
      </>
    );
  }
  if (entity.id) {
    // A table or an inbox about to go says how much goes with it.
    return (
      <>
        <EntityPill entity={entity} struck={struck} strong linked={!gone} />
        {struck && stage === "pending" && (
          <EntityCount kind={entity.kind} id={entity.id} />
        )}
        {struck && entity.kind === "knowledge_base" && (
          <KnowledgeBaseDocumentCount kbId={entity.id} />
        )}
      </>
    );
  }
  // A case named by its display id exists, so it opens its page too.
  if (entity.kind === "case" && entity.name) {
    return <EntityPill entity={entity} struck={struck} strong />;
  }
  return <NewEntity entity={entity} struck={struck} />;
}

function NewEntity({ entity, struck }: { entity: EntityRef; struck: boolean }) {
  const display = useEntityDisplay(entity);
  const { icon } = display;
  const every =
    !entity.name && entity.count === undefined
      ? ENTITY[entity.kind].every
      : undefined;
  const name =
    every ??
    (entity.count === undefined
      ? display.name
      : plural(
          entity.count,
          ENTITY[entity.kind].noun.toLowerCase().replace(/s$/, "")
        ));
  return (
    <>
      <EntityIcon kind={entity.kind} icon={icon} />
      <span
        className={cn(
          "min-w-0 truncate font-medium text-foreground",
          struck && "line-through decoration-red-400/70"
        )}
      >
        {name}
      </span>
    </>
  );
}

// Where the card's call stands. A decided card's rows have moved on since,
// so it shows no "before" read from them now.
export type CardStage = "pending" | "ran" | "denied";
const Stage = createContext<CardStage>("pending");

// What the card's header opens, set where the card has one: the result
// naming the same entity would only repeat it.
export const CardTarget = createContext<EntityRef | undefined>(undefined);

// A result is read from what the call returned; a result's parent the
// call was given (a test run's set) is read from its args.
function shownResults(
  view: ApprovalView,
  output: unknown,
  input: unknown,
  target: EntityRef | undefined
) {
  const returned = isRecord(output) ? output : {};
  const isTarget = (result: ArgField) =>
    result.type === "entity_ref" &&
    result.kind === target?.kind &&
    returned[result.arg] === target?.id;
  return {
    returned: { ...(isRecord(input) ? input : {}), ...returned },
    // A dry run rolled back what it returns.
    results: (returned.dry_run === true ? [] : (view.results ?? [])).filter(
      (result) => isShown(result, returned[result.arg]) && !isTarget(result)
    ),
  };
}

function Results({
  view,
  output,
  input,
}: {
  view: ApprovalView;
  output: unknown;
  input?: unknown;
}) {
  const target = useContext(CardTarget);
  const { returned, results } = shownResults(view, output, input, target);
  return results.map((result) => (
    <FieldValue
      key={`result:${result.arg}`}
      name={result.arg}
      display={result}
      values={returned}
      edit={false}
    />
  ));
}

// For a card drawn by its own renderer (a flow plan).
export function ResultList({
  view,
  output,
  input,
}: {
  view: ApprovalView;
  output: unknown;
  input?: unknown;
}) {
  const target = useContext(CardTarget);
  if (shownResults(view, output, input, target).results.length === 0)
    return null;
  return (
    <Stage.Provider value="ran">
      <FieldList className="mt-1.5">
        <Results view={view} output={output} input={input} />
      </FieldList>
    </Stage.Provider>
  );
}

export function EntityApprovalBody({
  view,
  input,
  output,
  stage = "pending",
  children,
}: {
  view: ApprovalView;
  // The call's args, which the view's fields read.
  input: unknown;
  output?: unknown;
  stage?: CardStage;
  // More rows a card adds after the fields, in the same label column.
  children?: ReactNode;
}) {
  const values = isRecord(input) ? input : {};
  const shown = useShownValues(view.entity, values);
  const pending = stage === "pending";
  const edit = view.op === "edit";
  const newName = view.rename_arg ? values[view.rename_arg] : undefined;
  // A heading that is the parent already names it: its field would repeat it.
  const repeatsHeading = (arg: string) =>
    headedByParent(view.entity) && arg === view.parent_arg;
  const replaces = view.replaces ?? {};
  const target = useContext(CardTarget);
  const hasResults =
    !pending && shownResults(view, output, input, target).results.length > 0;
  const warning = pending ? view.warning : undefined;
  // A decided card's entity has moved on: what it held then is not read now.
  const record = useCurrentRecord(
    view.entity,
    pending && Object.keys(replaces).length > 0
  );
  const replacedOf = (arg: string) =>
    record && replaces[arg] ? record[replaces[arg]] : undefined;
  const fields = (view.fields ?? []).filter(
    (field) =>
      isShown(field, values[field.arg], replacedOf(field.arg)) &&
      !repeatsHeading(field.arg)
  );
  const hasDetail =
    fields.length > 0 ||
    hasResults ||
    typeof newName === "string" ||
    !!warning ||
    !!children;
  return (
    <Stage.Provider value={stage}>
      <div className="flex items-start gap-2 pt-[6px] text-xs">
        {view.op && <OpBadge op={view.op} className="mt-0.5" />}
        <div className="min-w-0 flex-1">
          {view.entity && view.op && (
            <div className="flex min-w-0 items-center gap-2">
              <HeadEntity entity={view.entity} op={view.op} />
            </div>
          )}
          {hasDetail && (
            <FieldList className="mt-1.5">
              {typeof newName === "string" && view.entity && (
                <InlineRow
                  label="Name"
                  edit={edit}
                  // Once the call ran, the entity already has its new name.
                  from={
                    pending ? (
                      <EntityNameText entity={view.entity} />
                    ) : undefined
                  }
                >
                  {newName}
                </InlineRow>
              )}
              {fields.map((field) => (
                <FieldValue
                  key={field.arg}
                  name={field.arg}
                  display={field}
                  values={shown}
                  edit={edit}
                  replaced={replacedOf(field.arg)}
                />
              ))}
              {hasResults && (
                <Results view={view} output={output} input={input} />
              )}
              {warning && (
                <NoxusAlert
                  variant="destructive"
                  size="xs"
                  description={warning}
                />
              )}
              {children}
            </FieldList>
          )}
        </div>
      </div>
    </Stage.Provider>
  );
}
